export type Rect = { x: number; y: number; w: number; h: number }
type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

// Anchor the opposite edge/corner; side handles grow symmetrically along the
// constrained axis. Clamp the size before positioning, so edges never jump.
export function applyResize(handle: Handle, c: Rect, dx: number, dy: number, ratio: number | null, natural: { w: number; h: number }): Rect {
  const xDir = handle.includes('w') ? -1 : handle.includes('e') ? 1 : 0
  const yDir = handle.includes('n') ? -1 : handle.includes('s') ? 1 : 0
  const ax = xDir < 0 ? c.x + c.w : xDir > 0 ? c.x : c.x + c.w / 2
  const ay = yDir < 0 ? c.y + c.h : yDir > 0 ? c.y : c.y + c.h / 2
  const maxW = xDir < 0 ? ax : xDir > 0 ? natural.w - ax : 2 * Math.min(ax, natural.w - ax)
  const maxH = yDir < 0 ? ay : yDir > 0 ? natural.h - ay : 2 * Math.min(ay, natural.h - ay)
  let w = c.w + xDir * dx
  let h = c.h + yDir * dy
  if (ratio != null) {
    // Project corner movement onto the ratio diagonal. Both shrinking and
    // expansion follow the pointer instead of choosing the larger dimension.
    const wantedW = xDir && yDir
      ? (w + h / ratio) / (1 + 1 / (ratio * ratio))
      : xDir ? w : h * ratio
    const limit = Math.max(0, Math.min(maxW, maxH * ratio))
    w = clamp(wantedW, Math.min(20 * Math.max(1, ratio), limit), limit)
    h = w / ratio
  } else {
    w = clamp(w, Math.min(20, maxW), maxW)
    h = clamp(h, Math.min(20, maxH), maxH)
  }
  return {
    x: xDir < 0 ? ax - w : xDir > 0 ? ax : ax - w / 2,
    y: yDir < 0 ? ay - h : yDir > 0 ? ay : ay - h / 2,
    w, h,
  }
}
